/* ==========================================================================
   Gifty PH — GW-017 funnel instrumentation (audit 10.1's 11-event contract)

   Deliberately built against the CURRENT Globo form, right before Phase 2
   replaces it. This is the only honest before-baseline for the events the
   new form will emit -- without it the rebuild is unmeasurable.

   Zero PII: never reads or sends a Globo FIELD VALUE (name, story text,
   pronunciation notes, etc.). Only field LABELS ("Dedicated to", "Story"),
   step numbers/positions, timings, and counts. Every event carries
   form_version so it can be told apart from whatever the rebuilt form emits
   later under a different version tag.

   PLATFORM CONSTRAINT, not an oversight: `purchase` is not instrumented
   here. This theme has no checkout.liquid / order-status template -- this
   store uses Shopify's hosted checkout, which is outside theme code on this
   plan tier. `purchase` (and the standard view_item/add_to_cart/
   begin_checkout shapes) already fire via Shopify's native GA4 pixel
   (confirmed configured for G-KT7FVX3JGP with exactly these event types).
   That native pixel keeps running in parallel and is untouched by this file
   -- it just doesn't carry form_version, so it can't be used to build the
   Globo-specific funnel this contract is for. This file exists to add the
   10 events Shopify's native pixel has no way to know about.

   "Step" mapping, a deliberate call: the audit assumed a clean 3-step form.
   Globo's actual UI is a single form that reveals one field at a time (its
   own copy says so: "the next field will load automatically"), not 3
   distinct pages. Rather than force a fictional 3-step grouping onto data
   that doesn't have one, step_number/step_name here track each of Globo's
   10 individual fields in DOM order. This is MORE granular than the
   contract assumed, not less -- it can always be bucketed into 3 groups
   later during analysis, and it tells us exactly which field causes drop-
   off rather than which third of the form.
   ========================================================================== */

(function () {
  'use strict';

  var ctx = window.giftyAnalyticsContext || {};
  var FORM_VERSION = ctx.form_version || 'globo-v1';

  /* ------------------------------------------------------------------
     0. Helpers
     ------------------------------------------------------------------ */
  function deviceClass() {
    var w = window.innerWidth;
    if (w < 750) return 'mobile';
    if (w < 990) return 'tablet';
    return 'desktop';
  }

  // gtag.js is injected by a third-party app (Google & YouTube channel), not
  // this theme -- its load timing relative to this deferred script isn't
  // something we control. Rather than silently drop an event fired before
  // it's ready, queue briefly and retry, the same pattern the progress-bar
  // code above already uses for late-loading Globo elements.
  function track(eventName, params, retriesLeft) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, Object.assign({ device_class: deviceClass() }, params || {}));
      return;
    }
    retriesLeft = retriesLeft === undefined ? 10 : retriesLeft;
    if (retriesLeft <= 0) return;
    setTimeout(function () {
      track(eventName, params, retriesLeft - 1);
    }, 300);
  }

  /* ------------------------------------------------------------------
     1. view_item -- fires once per product-page load
     ------------------------------------------------------------------ */
  function trackViewItem() {
    if (ctx.page_template !== 'product' || !ctx.item_id) return;
    track('view_item', {
      item_id: ctx.item_id,
      item_name: ctx.item_name,
      price: ctx.price,
      currency: ctx.currency,
      page_template: ctx.page_template,
    });
  }

  /* ------------------------------------------------------------------
     2. Entry source -- which "start the form" trigger got the visitor here.
     Reads a value the Day 4 .js-start-song click handler stores in
     sessionStorage (see assets/custom-gifty.js initStartSongTriggers).
     Falls back to 'direct' if the visitor never clicked one of those
     (arrived on the PDP straight from an ad/search and just started typing).
     ------------------------------------------------------------------ */
  function getEntrySource() {
    try {
      return sessionStorage.getItem('gifty_entry_source') || 'direct';
    } catch (e) {
      return 'direct';
    }
  }

  /* ------------------------------------------------------------------
     3. Form field tracking: song_form_started, song_form_step_viewed,
     song_form_step_completed, song_form_error.

     Globo reveals fields one at a time. This watches for each new visible
     .gpo-input, treating its appearance as "step_viewed" and its first
     blur-with-a-value as "step_completed". field_name is the field's own
     label text (e.g. "Dedicated to") -- never its value.
     ------------------------------------------------------------------ */
  function initFormTracking() {
    var formStarted = false;
    var seenFields = new Map(); // field element -> { index, viewedAt, errorCount }
    var stepIndex = 0;

    function fieldLabel(field) {
      return field.getAttribute('name') || field.getAttribute('placeholder') || 'field_' + (seenFields.get(field) || {}).index;
    }

    function isFilled(field) {
      if (field.tagName === 'SELECT') return !!field.value;
      return !!(field.value && field.value.trim() !== '');
    }

    function looksInvalid(field) {
      // Best-effort: Globo's own invalid-state signals, generic enough to
      // survive a markup tweak without needing an exact class name.
      return (
        field.getAttribute('aria-invalid') === 'true' ||
        field.classList.contains('gpo-error') ||
        field.classList.contains('has-error') ||
        !!field.closest('.gpo-input-wrapper')?.querySelector('.gpo-error-message, .error-message')
      );
    }

    function onFieldSeen(field) {
      if (seenFields.has(field)) return;
      stepIndex += 1;
      seenFields.set(field, { index: stepIndex, viewedAt: Date.now(), errorCount: 0 });

      if (!formStarted) {
        formStarted = true;
        track('song_form_started', {
          form_version: FORM_VERSION,
          entry_source: getEntrySource(),
          occasion: null, // not yet filled at this point in Globo's field order; hook for a future occasion-prefill entry point
        });
      }

      track('song_form_step_viewed', {
        form_version: FORM_VERSION,
        step_number: stepIndex,
        step_name: fieldLabel(field),
      });
    }

    function onFieldBlur(event) {
      var field = event.target;
      if (!field || !field.classList || !field.classList.contains('gpo-input')) return;
      var state = seenFields.get(field);
      if (!state || state.completed) return;

      if (looksInvalid(field)) {
        state.errorCount += 1;
        track('song_form_error', {
          form_version: FORM_VERSION,
          field_name: fieldLabel(field),
          error_type: 'validation',
          step_number: state.index,
        });
        return;
      }

      if (isFilled(field)) {
        state.completed = true;
        track('song_form_step_completed', {
          form_version: FORM_VERSION,
          step_number: state.index,
          duration_ms: Date.now() - state.viewedAt,
          error_count: state.errorCount,
        });
      }
    }

    function scanForNewFields() {
      document.querySelectorAll('.gpo-input').forEach(function (field) {
        if (field.offsetParent !== null) onFieldSeen(field);
      });
    }

    // Found via live testing: on initial page load, Globo's own init script
    // briefly renders ALL fields visible before it applies the CSS/JS that
    // hides everything past the current one -- a scan that fires during that
    // window sees a false "6 fields visible at once" snapshot and marks all
    // of them seen prematurely, which then makes every later, real reveal a
    // no-op (already "seen"). A single MutationObserver callback can't tell
    // a transient in-progress DOM state from a settled one; debouncing can.
    // Every trigger (the observer, the initial retry loop) now goes through
    // this one debounced scan, which only actually runs once DOM mutations
    // have gone quiet for a moment -- by which point Globo's own init has
    // settled and offsetParent reflects reality.
    var debounceTimer = null;
    var pendingSince = null;
    function debouncedScan() {
      var now = Date.now();
      if (pendingSince === null) pendingSince = now;
      clearTimeout(debounceTimer);
      // Bounded: a genuinely busy page (other apps' scripts mutating the DOM
      // continuously during load) could otherwise keep resetting this timer
      // indefinitely and the scan would never run. Force it after 800ms of
      // pending regardless of whether mutations are still arriving.
      var wait = now - pendingSince > 800 ? 0 : 200;
      debounceTimer = setTimeout(function () {
        pendingSince = null;
        scanForNewFields();
      }, wait);
    }

    var observer = new MutationObserver(debouncedScan);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });

    document.addEventListener('focusin', function (e) {
      if (e.target.classList && e.target.classList.contains('gpo-input')) onFieldSeen(e.target);
    });
    document.addEventListener('focusout', onFieldBlur, true);
    document.addEventListener('change', onFieldBlur, true);

    // Retry the initial scan for up to ~10s in case Globo loads late.
    var retries = 0;
    (function initialScan() {
      debouncedScan();
      if (retries < 20 && seenFields.size === 0) {
        retries += 1;
        setTimeout(initialScan, 500);
      }
    })();

    initAbandonTracking(function () {
      return { formStarted: formStarted, seenFields: seenFields, stepIndex: stepIndex };
    });
  }

  /* ------------------------------------------------------------------
     4. song_form_abandoned -- fires at most once, on tab-hide or unload,
     only if the form was started and never completed (no add_to_cart yet).
     ------------------------------------------------------------------ */
  var addedToCart = false;
  function initAbandonTracking(getState) {
    var fired = false;

    function maybeFireAbandon() {
      if (fired || addedToCart) return;
      var state = getState();
      if (!state.formStarted) return;

      var completedCount = 0;
      state.seenFields.forEach(function (s) {
        if (s.completed) completedCount += 1;
      });

      fired = true;
      track('song_form_abandoned', {
        form_version: FORM_VERSION,
        last_step: state.stepIndex,
        completed_field_count: completedCount,
      });
    }

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') maybeFireAbandon();
    });
    window.addEventListener('pagehide', maybeFireAbandon);
  }

  /* ------------------------------------------------------------------
     5. add_to_cart -- hooked at the fetch layer, not Dawn's product-form
     pubsub. Globo may or may not submit through Dawn's own <product-form>
     component; every real add-to-cart, regardless of which app code paths
     triggered it, has to call Shopify's /cart/add endpoint. Hooking fetch
     is the one detection method that doesn't depend on which component did
     the submitting.
     ------------------------------------------------------------------ */
  function initAddToCartTracking() {
    var originalFetch = window.fetch;
    if (typeof originalFetch !== 'function') return;

    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url;
      var isCartAdd = url && /\/cart\/add(\.js)?(\?|$)/.test(url);

      var result = originalFetch.apply(this, arguments);
      if (!isCartAdd) return result;

      result
        .then(function (response) {
          if (!response.ok) return null;
          return response.clone().json();
        })
        .then(function (data) {
          if (!data || data.status) return; // Shopify error responses carry a "status" field
          addedToCart = true;
          track('add_to_cart', {
            form_version: FORM_VERSION,
            item_id: data.product_id,
            item_name: data.product_title,
            price: data.price ? data.price / 100 : undefined,
            quantity: data.quantity,
          });
        })
        .catch(function () {
          /* network/parse failure -- nothing to report */
        });

      return result;
    };
  }

  /* ------------------------------------------------------------------
     6. begin_checkout -- every [name="checkout"] button sitewide (cart
     page, cart drawer, cart notification popup all use this attribute).
     ------------------------------------------------------------------ */
  function initBeginCheckoutTracking() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[name="checkout"]');
      if (trigger) track('begin_checkout', { form_version: FORM_VERSION });
    });
  }

  /* ------------------------------------------------------------------
     7. support_click -- Messenger, email, and guarantee/FAQ links.
     ------------------------------------------------------------------ */
  function initSupportClickTracking() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link) return;

      var href = link.getAttribute('href');
      var topic = null;
      if (href.indexOf('m.me/') !== -1) topic = 'messenger';
      else if (href.indexOf('mailto:') === 0) topic = 'email';
      else if (href.indexOf('/pages/our-love-your-song-guarantee') !== -1) topic = 'guarantee';
      else if (href.indexOf('faq') !== -1) topic = 'faq';
      if (!topic) return;

      track('support_click', {
        location: link.closest('[id]')?.id || 'unknown',
        topic: topic,
        form_step: window.__giftyLastStepNumber || null,
      });
    });
  }

  /* ------------------------------------------------------------------
     8. sample_played -- the "Hear a sample" audio player.
     ------------------------------------------------------------------ */
  function initSamplePlayedTracking() {
    document.addEventListener(
      'play',
      function (e) {
        if (e.target.tagName !== 'AUDIO' && e.target.tagName !== 'VIDEO') return;
        track('sample_played', {
          asset_type: e.target.tagName.toLowerCase(),
          asset_id: e.target.id || e.target.currentSrc || 'unknown',
          location: e.target.closest('[id]')?.id || 'unknown',
          muted: e.target.muted,
        });
      },
      true
    );
  }

  /* ------------------------------------------------------------------
     9. Initialize
     ------------------------------------------------------------------ */
  function init() {
    trackViewItem();
    initFormTracking();
    initAddToCartTracking();
    initBeginCheckoutTracking();
    initSupportClickTracking();
    initSamplePlayedTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ------------------------------------------------------------------
     10. Public API — called by the theme-native form (gifty-song-form.js)
     ------------------------------------------------------------------ */
  window.giftyAnalytics = {
    viewItem: function (productId, price) {
      track('view_item', {
        form_version: FORM_VERSION,
        product_id: productId,
        price: price,
        currency: 'PHP'
      });
    },
    songFormStarted: function (productId) {
      track('song_form_started', { form_version: FORM_VERSION, product_id: productId });
    },
    songFormStepViewed: function (stepNumber, stepName) {
      track('song_form_step_viewed', {
        form_version: FORM_VERSION,
        step_number: stepNumber,
        step_name: stepName
      });
    },
    songFormStepCompleted: function (stepNumber, stepName) {
      track('song_form_step_completed', {
        form_version: FORM_VERSION,
        step_number: stepNumber,
        step_name: stepName
      });
    },
    songFormError: function (stepNumber, stepName, fieldName) {
      track('song_form_error', {
        form_version: FORM_VERSION,
        step_number: stepNumber,
        step_name: stepName,
        field_name: fieldName
      });
    },
    addToCart: function (productId, price, quantity, addonCount) {
      track('add_to_cart', {
        form_version: FORM_VERSION,
        product_id: productId,
        price: price,
        quantity: quantity,
        addons: addonCount
      });
    }
  };
})();
